import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const DefaultIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

const DEFAULT_CENTER: [number, number] = [46.603354, 1.888334];

type Props = {
  latitude: number | null;
  longitude: number | null;
  onChange: (lat: number, lng: number) => void;
  height?: number;
};

function MapClickHandler({ onPick }: { onPick: (lat: number, lng: number) => void }) {
  useMapEvents({
    click(e) {
      onPick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

const CityTransportMapPicker: React.FC<Props> = ({
  latitude,
  longitude,
  onChange,
  height = 220,
}) => {
  const center = useMemo((): [number, number] => {
    if (latitude != null && longitude != null && !Number.isNaN(latitude) && !Number.isNaN(longitude)) {
      return [latitude, longitude];
    }
    return DEFAULT_CENTER;
  }, [latitude, longitude]);

  const [mapKey, setMapKey] = useState(0);
  useEffect(() => {
    setMapKey((k) => k + 1);
  }, [center[0], center[1]]);

  const markerPos = useMemo((): [number, number] | null => {
    if (latitude != null && longitude != null && !Number.isNaN(latitude) && !Number.isNaN(longitude)) {
      return [latitude, longitude];
    }
    return null;
  }, [latitude, longitude]);

  const handlePick = useCallback(
    (lat: number, lng: number) => {
      onChange(lat, lng);
    },
    [onChange]
  );

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        Cliquez sur la carte pour placer le centre de la ville (zone transport). Les coordonnées sont enregistrées
        sur cette source.
      </Typography>
      <MapContainer
        key={mapKey}
        center={center}
        zoom={markerPos ? 12 : 6}
        style={{ height, width: '100%', borderRadius: 8 }}
        scrollWheelZoom
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MapClickHandler onPick={handlePick} />
        {markerPos && <Marker position={markerPos} draggable eventHandlers={{ dragend: (e) => {
          const p = e.target.getLatLng();
          onChange(p.lat, p.lng);
        } }} />}
      </MapContainer>
    </Box>
  );
};

export default CityTransportMapPicker;
